import Minus from 'components/ReusableComponents/Icons/Minus'
import Plus from 'components/ReusableComponents/Icons/Plus'
import { useEffect, useMemo, useRef, useState } from 'react'

import styles from './MultiSelectDropdown.module.scss'

const MultiSelectDropdown = ({
  value,
  options,
  onChangeValue,
  itemName,
  itemNamePlural,
  className,
  hasUnselectButton = false,
  zeroResultText = null,
  searchable = false
}) => {
  const [isOpen, setOpen] = useState(false)
  const [pmsList, setPmsList] = useState([])
  const [searchString, setSearchString] = useState('')

  function handleCheckboxChange(e) {
    if (e.target.checked) {
      onChangeValue([...value, e.target.value])
    } else {
      onChangeValue([...value].filter(v => v !== e.target.value))
    }
  }

  const node = useRef()

  const handleClickOutside = e => {
    if (node.current.contains(e.target)) {
      return
    }
    setOpen(false)
  }

  const sortedKeys = useMemo(() => {
    const keys = Object.keys(options || {})
    if (searchable) {
      return keys.sort((a, b) =>
        String(options[a]).localeCompare(String(options[b]), undefined, { sensitivity: 'base' })
      )
    }
    return keys.sort((a, b) => options[b] - options[a])
  }, [options, searchable])

  const filteredKeys = useMemo(() => {
    if (!searchable || !searchString.trim()) {
      return sortedKeys
    }
    const q = searchString.trim().toLowerCase()
    return sortedKeys.filter(k => {
      const label = String(options[k] ?? '').toLowerCase()
      return label.includes(q) || String(k).toLowerCase().includes(q)
    })
  }, [searchable, searchString, sortedKeys, options])

  useEffect(() => {
    setPmsList(Object.keys(options || {}))
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, options])

  useEffect(() => {
    if (!isOpen) {
      setSearchString('')
    }
  }, [isOpen])

  return (
    <div className={styles.wrapper} ref={node}>
      <div className={styles.dropdownHolder} onClick={() => setOpen(!isOpen)}>
        <div
          className={[styles.select, className].join(' ')}
          title={
            value.length === 1
              ? String(options[value[0]] ?? '')
              : value.length > 1
                ? `${value.length} ${itemNamePlural || itemName} selected`
                : undefined
          }
        >
          {value.length === 0 && (zeroResultText || `0 ${itemName}`)}
          {value.length === 1 && options[value[0]]}
          {value.length > 1 && `${value.length} ${itemNamePlural || itemName}`}
        </div>
      </div>
      {isOpen && (
        <div className={[styles.dialog, searchable ? styles.dialogWide : ''].filter(Boolean).join(' ')}>
          {searchable && (
            <div className={styles.searchFilterHolder}>
              <input
                type="search"
                className={styles.searchFilter}
                value={searchString}
                onChange={e => setSearchString(e.target.value)}
                placeholder="Type to filter list..."
                aria-label={`Filter ${itemName} list`}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                autoFocus
              />
              {searchString.trim() ? (
                <div className={styles.searchSummary}>
                  Showing {filteredKeys.length} of {sortedKeys.length}
                  {filteredKeys.length === 1 ? ' row' : ' rows'}
                </div>
              ) : null}
            </div>
          )}
          <div className={styles.dialogBody}>
            {filteredKeys.length === 0 && searchable && searchString.trim() && (
              <div className={styles.noMatches}>No matches for this search. Try different words.</div>
            )}
            {filteredKeys.map(v => (
              <Value
                isChecked={value.includes(v)}
                key={v}
                value={v}
                text={options[v]}
                onChange={handleCheckboxChange}
              />
            ))}
          </div>
          <div className={styles.dialogFooter}>
            {pmsList.length > value.length && (
              <button type="button" className={styles.checkAll} onClick={() => onChangeValue(pmsList)}>
                <Plus /> Check all
              </button>
            )}
            {hasUnselectButton && value.length > 1 && (
              <button type="button" className={styles.uncheckAll} onClick={() => onChangeValue([])}>
                <Minus /> Uncheck all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const Value = ({ value, onChange, isChecked, text }) => (
  <div className={isChecked ? styles.rateActive : styles.rate}>
    <label className={styles.rateName} title={String(text ?? '')}>
      <input type="checkbox" onChange={onChange} checked={isChecked} value={value} />
      {text}
    </label>
  </div>
)

export default MultiSelectDropdown
